import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, TextField, Button, Stack, CircularProgress } from '@mui/material';
import { Science, ArrowForward, CheckCircleOutline, LockOutlined } from '@mui/icons-material';
import { motion } from 'framer-motion';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const GateScreen = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [msg, setMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setStatus('error'); setMsg('Escribe un correo válido.');
      return;
    }
    setStatus('loading'); setMsg('');
    try {
      const r = await fetch('/api/access-request', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setStatus('done'); setMsg(d.message || ''); }
      else if (r.status === 429) { setStatus('error'); setMsg('Demasiados intentos. Inténtalo más tarde.'); }
      else { setStatus('error'); setMsg(d.error || 'No se pudo enviar la solicitud.'); }
    } catch {
      setStatus('error'); setMsg('No se pudo conectar. Inténtalo de nuevo.');
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', width: '100%', position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: '#06241d', color: '#eafaf3', px: 2, py: 6,
    }}>
      {/* Resplandores de fondo */}
      <Box component={motion.div}
        animate={{ opacity: [0.35, 0.6, 0.35], scale: [1, 1.08, 1] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        sx={{ position: 'absolute', top: '-15%', left: '-10%', width: 520, height: 520, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(29,158,117,.45), transparent 65%)', filter: 'blur(20px)', pointerEvents: 'none' }} />
      <Box component={motion.div}
        animate={{ opacity: [0.25, 0.5, 0.25], scale: [1.05, 1, 1.05] }}
        transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
        sx={{ position: 'absolute', bottom: '-20%', right: '-12%', width: 560, height: 560, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,224,192,.30), transparent 65%)', filter: 'blur(20px)', pointerEvents: 'none' }} />

      <Box component={motion.div}
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
        sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 540, textAlign: 'center' }}>

        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 4 }}>
          <Science sx={{ color: '#7ce0c0' }} />
          <Typography sx={{ fontWeight: 700, letterSpacing: '.02em', fontSize: '1.15rem' }}>SciDataHub</Typography>
        </Stack>

        <Typography sx={{ fontSize: '.72rem', letterSpacing: '.28em', color: '#7ce0c0', mb: 2.5, fontWeight: 600 }}>
          BETA PRIVADA · POR INVITACIÓN
        </Typography>

        <Typography component="h1" sx={{
          fontFamily: 'Georgia, serif', fontWeight: 700, lineHeight: 1.1,
          fontSize: 'clamp(2rem, 6vw, 3.1rem)', mb: 2.5, letterSpacing: '-0.01em',
        }}>
          La investigación no debería<br />morir en un PDF.
        </Typography>

        <Typography sx={{ color: 'rgba(234,250,243,.72)', fontSize: '1.02rem', lineHeight: 1.7, mb: 4, maxWidth: 460, mx: 'auto' }}>
          Estamos construyendo un lugar donde la ciencia se vuelve <b style={{ color: '#eafaf3' }}>reproducible, ejecutable
          y certificada</b> — modelos vivos que cualquiera puede explorar. El acceso se abre poco a poco.
        </Typography>

        {status === 'done' ? (
          <Box component={motion.div} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            sx={{ bgcolor: 'rgba(124,224,192,.10)', border: '1px solid rgba(124,224,192,.35)', borderRadius: 3, p: 3.5, maxWidth: 440, mx: 'auto' }}>
            <CheckCircleOutline sx={{ color: '#7ce0c0', fontSize: 40, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 0.5 }}>Estás en la lista.</Typography>
            <Typography sx={{ color: 'rgba(234,250,243,.72)', fontSize: '.95rem', lineHeight: 1.6 }}>
              {msg || 'Te escribiremos en cuanto se abra tu acceso.'} Revisa tu correo (y la carpeta de spam).
            </Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={submit} sx={{ maxWidth: 460, mx: 'auto' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                fullWidth type="email" placeholder="tu@correo.com" value={email}
                onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
                disabled={status === 'loading'} autoComplete="email"
                sx={{
                  '& .MuiOutlinedInput-root': { color: '#eafaf3', bgcolor: 'rgba(255,255,255,.06)', borderRadius: 2 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(124,224,192,.35)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(124,224,192,.6)' },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7ce0c0' },
                  '& input::placeholder': { color: 'rgba(234,250,243,.5)' },
                }}
              />
              <Button type="submit" variant="contained" disabled={status === 'loading'}
                endIcon={status === 'loading' ? <CircularProgress size={16} sx={{ color: '#06241d' }} /> : <ArrowForward />}
                sx={{ whiteSpace: 'nowrap', px: 3, py: 1.5, borderRadius: 2, fontWeight: 700,
                  bgcolor: '#7ce0c0', color: '#06241d', '&:hover': { bgcolor: '#9fe9d2' } }}>
                Solicitar acceso
              </Button>
            </Stack>
            {status === 'error' && (
              <Typography sx={{ color: '#ffb4a8', fontSize: '.85rem', mt: 1.5, textAlign: 'left' }}>{msg}</Typography>
            )}
          </Box>
        )}

        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mt: 5, color: 'rgba(234,250,243,.6)' }}>
          <LockOutlined sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '.9rem' }}>
            ¿Ya tienes acceso?{' '}
            <Link to="/login" style={{ color: '#7ce0c0', fontWeight: 600, textDecoration: 'none' }}>Inicia sesión</Link>
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default GateScreen;
